import { type ApolloCache, useMutation } from "@apollo/client";
import Archive from "@mui/icons-material/Archive";
import DoneAll from "@mui/icons-material/DoneAll";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import MoreVert from "@mui/icons-material/MoreVert";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { MyNotificationsQuery } from "generated-graphql/graphql";
import { useGridSelection } from "hooks/omnisearch-v2";
import {
  ARCHIVE_NOTIFICATIONS,
  GET_MY_NOTIFICATIONS,
  GET_MY_NOTIFICATION_VIEWS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATIONS_READ,
  MARK_NOTIFICATIONS_UNREAD,
} from "hooks/useNotifications";
import { Fragment, useState } from "react";

type NotificationRow = MyNotificationsQuery["myNotifications"]["items"][number];

function updateNotificationsReadAtInCache(
  cache: ApolloCache<unknown>,
  notificationUserIds: string | string[] | null | undefined,
  readAt: string | null
) {
  if (!notificationUserIds) {
    return;
  }
  const ids = Array.isArray(notificationUserIds) ? notificationUserIds : [notificationUserIds];
  ids.forEach((id) => {
    cache.modify({
      id: cache.identify({ __typename: "NotificationUser", id }),
      fields: { readAt: () => readAt },
    });
  });
}

/**
 * Bulk actions for the customer notifications inbox.
 * Subscribes to grid selection via useGridSelection so the parent grid does not re-render.
 */
export function NotificationBulkActions() {
  const { selectedCount, getSelectedRows, getSelectedIds, clearSelection } =
    useGridSelection<NotificationRow>();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const refetchQueries = [
    GET_MY_NOTIFICATIONS,
    GET_MY_UNREAD_NOTIFICATION_COUNT,
    GET_MY_NOTIFICATION_VIEWS,
  ];
  const countRefetchQueries = [GET_MY_UNREAD_NOTIFICATION_COUNT, GET_MY_NOTIFICATION_VIEWS];

  const [markManyRead, markManyReadState] = useMutation(MARK_NOTIFICATIONS_READ, {
    refetchQueries: countRefetchQueries,
    update: (cache, _result, { variables }) =>
      updateNotificationsReadAtInCache(
        cache,
        variables?.notificationUserIds,
        new Date().toISOString()
      ),
  });
  const [markManyUnread, markManyUnreadState] = useMutation(MARK_NOTIFICATIONS_UNREAD, {
    refetchQueries: countRefetchQueries,
    update: (cache, _result, { variables }) =>
      updateNotificationsReadAtInCache(cache, variables?.notificationUserIds, null),
  });
  const [archiveMany, archiveManyState] = useMutation(ARCHIVE_NOTIFICATIONS, {
    refetchQueries,
  });
  const [markAllRead, markAllReadState] = useMutation(MARK_ALL_NOTIFICATIONS_READ, {
    refetchQueries,
  });

  const isBulkMutating =
    markManyReadState.loading ||
    markManyUnreadState.loading ||
    archiveManyState.loading ||
    markAllReadState.loading;

  const selectedRows = getSelectedRows();
  const allSelectedRead =
    selectedRows.length > 0 && selectedRows.every((row) => Boolean(row.readAt));

  const runBulkAction = async (
    action: (variables: { notificationUserIds: string[] }) => Promise<unknown>
  ) => {
    const notificationUserIds = getSelectedIds().map((id) => String(id));
    if (notificationUserIds.length === 0) {
      return;
    }
    try {
      await action({ notificationUserIds });
    } finally {
      clearSelection();
    }
  };

  const handleBulkMarkRead = () => runBulkAction((variables) => markManyRead({ variables }));
  const handleBulkMarkUnread = () => runBulkAction((variables) => markManyUnread({ variables }));
  const handleBulkArchive = () => runBulkAction((variables) => archiveMany({ variables }));

  const handleMarkAllRead = async () => {
    try {
      await markAllRead({ variables: {} });
    } finally {
      clearSelection();
    }
  };

  return (
    <Fragment key="bulk-actions">
      <Tooltip title={allSelectedRead ? "Mark as unread" : "Mark as read"}>
        <span>
          <IconButton
            size="small"
            onClick={allSelectedRead ? handleBulkMarkUnread : handleBulkMarkRead}
            disabled={selectedCount === 0 || isBulkMutating}
          >
            {allSelectedRead ? (
              <MarkEmailUnread fontSize="small" />
            ) : (
              <MarkEmailRead fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Archive">
        <span>
          <IconButton
            size="small"
            color="error"
            onClick={handleBulkArchive}
            disabled={selectedCount === 0 || isBulkMutating}
          >
            <Archive fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="More actions">
        <span>
          <IconButton
            size="small"
            aria-label="More notification actions"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            disabled={isBulkMutating}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            void handleMarkAllRead();
          }}
          disabled={isBulkMutating}
        >
          <ListItemIcon>
            <DoneAll fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark all as read</ListItemText>
        </MenuItem>
      </Menu>
    </Fragment>
  );
}
